import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Calendar, Users, AlertTriangle, Clock, ArrowRight, MessageSquare, CreditCard, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MiniCalendar from "@/components/MiniCalendar";

function StatCard({ label, value, sublabel, accent, testid }) {
  return (
    <div className="card-flat" data-testid={testid}>
      <div className="label">{label}</div>
      <div className={`font-serif text-6xl tabular leading-none mt-1 ${accent ? "" : ""}`}>{value}</div>
      {sublabel && (
        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-2">
          {sublabel}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSuper = user?.role === "superadmin";

  useEffect(() => {
    if (isSuper) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get("/dashboard");
        setData(res.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [isSuper]);

  // Super admin n'a pas de données personnelles — on les redirige vers leur écran de gestion.
  if (isSuper) {
    return <Navigate to="/utilisateurs" replace />;
  }

  if (loading) {
    return (
      <div data-testid="dashboard-page">
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">Chargement…</p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-10">
        <div className="section-number">00 / Accueil</div>
        <h1 className="h1-big">Ton tableau de bord.</h1>
        <p className="text-sm text-[var(--muted)] mt-3 max-w-xl leading-relaxed">
          Aperçu rapide de tes événements et de tes officiels.
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard
          label="Événements"
          value={data.events.total}
          sublabel={`${data.events.open} actif${data.events.open > 1 ? "s" : ""} · ${data.events.closed} fermé${data.events.closed > 1 ? "s" : ""}`}
          testid="stat-events"
        />
        <StatCard
          label="Officiels"
          value={data.officials_count}
          sublabel="dans ta banque"
          testid="stat-officials"
        />
        <StatCard
          label="Parties totales"
          value={data.total_games}
          sublabel="toutes années confondues"
          testid="stat-games"
        />
        <StatCard
          label="Sans arbitre"
          value={data.games_missing_refs}
          sublabel={data.games_missing_refs > 0 ? "à assigner" : "tout est assigné"}
          testid="stat-missing-refs"
        />
      </div>

      {/* Actions / Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="card-flat" data-testid="alert-availabilities">
          <MessageSquare size={18} strokeWidth={2.5} className="mb-2" />
          <div className="label mb-1">Dispos en attente</div>
          <div className="font-serif text-4xl tabular leading-none">{data.pending_availabilities}</div>
          <p className="text-xs text-[var(--muted)] mt-2 leading-relaxed">
            {data.pending_availabilities === 0
              ? "Aucune demande en attente."
              : "Officiels qui n'ont pas encore répondu à un SMS de dispos."}
          </p>
        </div>
        <div className="card-flat" data-testid="alert-unpaid">
          <CreditCard size={18} strokeWidth={2.5} className="mb-2" />
          <div className="label mb-1">À payer</div>
          <div className="font-serif text-4xl tabular leading-none">{data.unpaid_officials_count}</div>
          <p className="text-xs text-[var(--muted)] mt-2 leading-relaxed">
            {data.unpaid_officials_count === 0
              ? "Tout le monde est payé sur les événements fermés."
              : "Assignations non marquées payées dans tes événements fermés."}
          </p>
          <Link to="/paies" className="font-mono text-[10px] uppercase tracking-widest hover:text-black inline-flex items-center gap-1 mt-3">
            Voir les paies <ArrowRight size={11} strokeWidth={2.5} />
          </Link>
        </div>
        <div className="card-flat" data-testid="quick-actions">
          <Plus size={18} strokeWidth={2.5} className="mb-2" />
          <div className="label mb-1">Raccourcis</div>
          <div className="space-y-1.5 mt-2">
            <Link to="/evenements" className="block font-mono text-[10px] uppercase tracking-widest hover:text-black">
              <ArrowRight size={11} strokeWidth={2.5} className="inline mr-1" /> Tous mes événements
            </Link>
            <Link to="/officiels" className="block font-mono text-[10px] uppercase tracking-widest hover:text-black">
              <ArrowRight size={11} strokeWidth={2.5} className="inline mr-1" /> Gérer les officiels
            </Link>
            <Link to="/reglages" className="block font-mono text-[10px] uppercase tracking-widest hover:text-black">
              <ArrowRight size={11} strokeWidth={2.5} className="inline mr-1" /> Réglages & forfaits
            </Link>
          </div>
        </div>
      </div>

      {/* Calendar + Upcoming events side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
        <MiniCalendar marks={data.calendar_marks || []} />
        <div>
          <div className="flex items-end justify-between mb-3 border-b-2 border-black pb-1.5">
            <div>
              <div className="label">Prochains événements</div>
              <h2 className="font-serif text-2xl leading-tight">À venir.</h2>
            </div>
            <Link to="/evenements" className="font-mono text-[10px] uppercase tracking-widest hover:text-black inline-flex items-center gap-1">
              Tous <ArrowRight size={11} strokeWidth={2.5} />
            </Link>
          </div>
          {data.upcoming.length === 0 ? (
            <p className="text-sm text-[var(--muted)] italic">Aucun événement à venir.</p>
          ) : (
            <div className="space-y-3" data-testid="upcoming-list">
              {data.upcoming.map((e) => (
                <Link
                  key={e.id}
                  to={`/evenements/${e.id}`}
                  className="card-flat block hover:border-black transition-colors"
                  data-testid={`upcoming-${e.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-sm leading-tight">{e.name}</h3>
                    <Calendar size={14} strokeWidth={2.5} className="text-[var(--muted)] shrink-0" />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] tabular">{e.date}</div>
                  {e.location && (
                    <div className="text-xs text-[var(--muted)] mt-1">{e.location}</div>
                  )}
                  {e.client && (
                    <div className="text-xs text-[var(--muted)]">Client: {e.client}</div>
                  )}
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[var(--border)]">
                    <span className="font-mono text-[10px] uppercase tracking-widest">
                      <span className="tabular font-bold text-black">{e.games_count}</span> partie{e.games_count > 1 ? "s" : ""}
                    </span>
                    {e.missing_refs > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-black font-mono text-[10px] uppercase tracking-widest" style={{ backgroundColor: "#dbeafe" }}>
                        <AlertTriangle size={10} strokeWidth={2.5} /> {e.missing_refs} sans arbitre
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
